import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { isNil, isNull } from "lodash";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, useApiMutation } from "../hooks/useApi";
import { TANotification } from "../types/TANotification";
import { Client } from "../types/client";
import { Profile } from "../types/contact";
import { GoogleMapsAutoComplete } from "./GoogleAutocomplete";

interface Props {}

const ClientData: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { data: profile, isLoading: isProfileLoading } =
    useApi<Profile>("profile");
  const [filteredClients, setFilteredClients] = useState<Client[]>();
  const { data: notifications, isLoading: isNotificationsLoading } =
    useApi<TANotification[]>("notifications");
  const { apiUpdate } = useApiMutation();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<boolean>(false);
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [placeId, setPlaceId] = useState<string>();

  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [selectedAddress, setSelectedAddress] = useState<string>();
  const nameIsDifferent = name !== selectedClient?.name; // checks if the name has actually been modified from the original name
  const addressIsDifferent = selectedAddress !== selectedClient?.fullAddress;
  const detailsChanged = nameIsDifferent || addressIsDifferent ? true : false;

  const [nameError, setNameError] = useState<boolean>(false);
  const [lengthError, setLengthError] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<boolean>(false);
  const [addressLengthError, setAddressLengthError] = useState<boolean>(false);
  const isErrored =
    nameError || lengthError || addressError || addressLengthError
      ? true
      : false;

  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const showNotifications = notifications?.results?.filter(
    (n) => n.type === 1 && n.sid === selectedClient?.id
  );
  const updateSubmitted = notifications?.results?.some(
    (n) => n.type === 1 && n.sid === selectedClient?.id
  );
  const isLoading = isNotificationsLoading;

  useEffect(() => {
    if (!isProfileLoading) {
      const filteredClients = profile?.results?.clients?.filter(
        (client) => client.isPrimary
      );
      setFilteredClients(filteredClients);

      if (filteredClients && filteredClients.length > 0) {
        const defaultClientId = filteredClients[0].id;
        const defaultSelectedClient = filteredClients.find(
          (client) => client.id === defaultClientId
        );
        setSelectedClient(defaultSelectedClient);
        setName(defaultSelectedClient!.name);
        setSelectedAddress(defaultSelectedClient!.fullAddress);
      } else {
        setSelectedClient(undefined);
      }
    }
  }, [isProfileLoading, profile?.results?.clients]);

  const handleChangeClient = (event: SelectChangeEvent) => {
    const clientId = event.target.value as string;
    const newSelectedClient = filteredClients?.find(
      (client) => client.id === clientId
    );
    setSelectedClient(newSelectedClient);
    setName(newSelectedClient!.name);
    setDataChanged(false);
    setSelectedAddress(newSelectedClient?.fullAddress);
  };

  const sendUpdate = useCallback(() => {
    apiUpdate.mutate(
      {
        entity: "update",
        post: {
          clientDetails: {
            clientId: selectedClient?.id,
            name,
            placeId,
          },
        },
        refetchType: "none",
      },
      {
        onSettled: () => {
          setSaving(false);
          queryClient.invalidateQueries({
            queryKey: ["notifications"],
          });
        },
      }
    );
  }, [apiUpdate, selectedClient?.id, name, placeId, queryClient]);

  return (
    <>
      {!isLoading && (
        <Stack spacing={4} padding={isSmallScreen ? 0 : 1}>
          {showNotifications?.map((notification, index) => (
            <Alert key={`client-notification-${index}`} severity="info">
              {/* {notification.message} */}
              {t("alerts.clientChange")}
            </Alert>
          ))}

          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            paddingTop={1}
            paddingBottom={0}
          >
            {filteredClients && filteredClients.length > 1 ? (
              <>
                {/* {selectedClient?.id} */}
                <FormControl sx={{ width: isSmallScreen ? "100%" : "30%" }}>
                  <InputLabel> {t("adminPage.client")} </InputLabel>
                  <Select
                    size="small"
                    value={selectedClient?.id || ""}
                    label={t("adminPage.client")}
                    onChange={handleChangeClient}
                  >
                    {filteredClients &&
                      filteredClients.map((client) => (
                        <MenuItem
                          key={`clientList-${client.id}`}
                          value={client.id}
                        >
                          {client.name}
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>{t("clientPage.text")}</FormHelperText>
                </FormControl>
              </>
            ) : (
              <Typography variant="h6" sx={{ m: "10px 0 10px 0" }}>
                {filteredClients &&
                  filteredClients.length > 0 &&
                  filteredClients[0].name}{" "}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ m: 0, p: "1px", width: "100%" }} />

          <TextField
            required
            error={nameError || lengthError}
            helperText={
              nameError
                ? "Client name is required"
                : lengthError
                ? "Client name is too long, maximum length is 256 characters"
                : ""
            }
            label={t("clientPage.clientName")}
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
              setDataChanged(true);
              if (event.target.value.length > 256) {
                setLengthError(true);
                setNameError(false);
              } else {
                setLengthError(false);
                setNameError(event.target.value.length === 0);
              }
            }}
            fullWidth
            inputProps={{
              maxLength: 257,
            }}
          />

          <GoogleMapsAutoComplete
            addressLengthError={addressLengthError}
            setAddressLengthError={setAddressLengthError}
            addressError={addressError}
            setAddressError={setAddressError}
            label={t("clientPage.address")}
            onPlaceSelect={(result) => {
              setSelectedAddress(result?.description);
              setDataChanged(!isNil(result));
              if (!isNull(result?.place_id)) {
                setPlaceId(result?.place_id);
              }
            }}
            defaultValue={selectedClient?.fullAddress || ""}
          />
          <br />
          <Box>
            <Button
              disabled={
                saving ||
                updateSubmitted ||
                !dataChanged ||
                !detailsChanged ||
                isErrored
              }
              variant="contained"
              onClick={() => {
                setSaving(true);
                sendUpdate();
              }}
            >
              {t("buttons.submit")}
            </Button>
          </Box>
        </Stack>
      )}
    </>
  );
};

export default ClientData;
